// "Check it over" (wireframe 22): the one screen every way of adding or
// editing a recipe ends on. Each part has Edit (back to that step), what
// needs a look is listed at the top, and saving is allowed with flags still
// open, except a missing name.
import type { ReactNode } from "react";
import { COLORS, DIET_TAG_LABELS, FONT_SIZE, RADIUS, RECIPE_TYPE_LABELS, SPACE } from "../../lib/constants";
import { prettyFractions } from "../../lib/recipeDisplay";
import { formatMinutes } from "../../lib/utils";
import type { EntryIngredient, EntryStep, Flag } from "../../lib/recipeEntry";
import type { Recipe } from "../../lib/types";
import { smallLabel, supporting } from "../recipeStyles";
import { textButton } from "../plan/planStyles";
import { isYieldType, stepHeading } from "./writeStyles";

const siteName = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

export function RecipeReview({ recipe, flags, unclear, importedFrom, onEdit }: { recipe: Recipe; flags: Flag[]; unclear: string[]; importedFrom: string | null; onEdit: (step: 1 | 2 | 3 | 4) => void }) {
  const ingredients = ((recipe.ingredients || []) as EntryIngredient[]).filter((i) => (i.name || "").trim() && i.type !== "note");
  const steps = ((recipe.instructions || []) as EntryStep[]).filter((s) => (s.text || "").trim() && s.type !== "note");
  const basics = [
    (RECIPE_TYPE_LABELS as Record<string, string>)[recipe.recipeType] ?? recipe.recipeType,
    ...(recipe.mealTypes || []).map((m) => m.charAt(0).toUpperCase() + m.slice(1)),
    isYieldType(recipe) && recipe.yield ? `makes ${recipe.yield}` : `serves ${recipe.servings || "?"}`,
    recipe.activeTime ? `${formatMinutes(recipe.activeTime)} active` : "",
    recipe.cookTime ? `${formatMinutes(recipe.cookTime)} cook` : "",
  ].filter(Boolean);
  const nutrition = recipe.caloriesPerServing || recipe.proteinPerServing || recipe.carbsPerServing || recipe.fatPerServing;
  const details = [
    recipe.source || recipe.sourceUrl ? `Source: ${recipe.source || siteName(recipe.sourceUrl || "")}${recipe.sourceUrl ? ", with link" : ""}` : "No source",
    recipe.cuisine || "",
    (recipe.seasons || []).join(", "),
    (recipe.dietTags || []).map((d) => (DIET_TAG_LABELS as Record<string, string>)[d]).join(", "),
    nutrition ? "nutrition" : "no nutrition",
    recipe.isPublic ? "public" : "",
  ].filter(Boolean);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE.xxl }}>
      <div>
        {importedFrom && <div style={supporting}>Imported from {siteName(importedFrom)}</div>}
        <h1 style={stepHeading}>Check it over</h1>
        <p style={{ ...supporting, margin: "var(--space-xs) 0 0" }}>Nothing is saved until you tap Save.</p>
      </div>

      {flags.length > 0 && (
        <div role="status" style={{ border: `1px solid ${COLORS.caution}`, borderRadius: RADIUS.xl, padding: "var(--space-lg) var(--space-xl)" }}>
          <strong style={{ color: COLORS.caution }}>
            {flags.length} thing{flags.length === 1 ? "" : "s"} need{flags.length === 1 ? "s" : ""} a look
          </strong>
          <ul style={{ margin: "var(--space-xs) 0 0", paddingLeft: "var(--space-xxl)", fontSize: FONT_SIZE.sm }}>
            {flags.map((f) => (
              <li key={f.text}>{f.text}</li>
            ))}
          </ul>
        </div>
      )}

      <Part title="Basics" onEdit={() => onEdit(1)}>
        <div style={{ fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.h3, fontWeight: 600 }}>{recipe.title || <span style={{ color: COLORS.danger }}>No name yet</span>}</div>
        <div style={supporting}>{basics.join(" · ")}</div>
      </Part>

      <Part title={`Ingredients · ${ingredients.length}`} onEdit={() => onEdit(2)}>
        {ingredients.slice(0, 3).map((i) => (
          <div key={i.id} style={{ fontSize: FONT_SIZE.base }}>
            <strong>{[prettyFractions(i.quantity || ""), i.unit].filter(Boolean).join(" ")}</strong> {i.name}
            {i.prep ? `, ${i.prep}` : ""}
            {unclear.includes(i.id) && <span style={{ color: COLORS.caution, fontWeight: 700 }}> · amount unclear</span>}
          </div>
        ))}
        {ingredients.length > 3 && <div style={supporting}>and {ingredients.length - 3} more</div>}
      </Part>

      <Part title={`Steps · ${steps.length}`} onEdit={() => onEdit(3)}>
        {steps.slice(0, 2).map((s, n) => (
          <div key={s.id} style={{ fontSize: FONT_SIZE.base, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {n + 1}. {s.text}
          </div>
        ))}
        {steps.length > 2 && <div style={supporting}>and {steps.length - 2} more</div>}
      </Part>

      <Part title="Details" onEdit={() => onEdit(4)}>
        <div style={supporting}>{details.join(" · ")}</div>
      </Part>
    </div>
  );
}

function Part({ title, onEdit, children }: { title: string; onEdit: () => void; children: ReactNode }) {
  return (
    <section style={{ borderTop: `1px solid ${COLORS.line}`, paddingTop: SPACE.md, display: "flex", flexDirection: "column", gap: SPACE.xs }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ ...smallLabel, color: COLORS.soft, margin: 0 }}>{title}</h2>
        <button type="button" onClick={onEdit} style={{ ...textButton, fontSize: FONT_SIZE.sm }}>
          Edit
        </button>
      </div>
      {children}
    </section>
  );
}
